import React from 'react';
import PropTypes from 'prop-types';
import { List, ListItem, ListItemText, FormGroup, Typography, ListSubheader } from '@material-ui/core';
import cx from 'classnames';
import { Arrow, CheckIcon } from 'sharedComponents/Icons';
import { STORE_MANAGER_LIST, BRAND_PROFILES_LIST } from 'constants/index';
import Drawer from '../Drawer';

const StoreSelection = ({
  classes,
  setSelectedStore,
  onSelectStore,
  selectedStore,
  brand,
  headerMode,
  selectedStoreId,
  isSilentMode,
  isStoreSelectorOpen,
  onStoreSelectorClose,
  isUniversalSearch,
}) => {
  const [drawer, setDrawer] = React.useState(null);

  const onUpdateClick = ([storeId, storeName]) => {
    if (isSilentMode) {
      onSelectStore([storeId, storeName]);
      return;
    }

    setSelectedStore({
      brand,
      storeId,
      storeName,
    });
    onSelectStore();
    setDrawer(null);
  };

  const storeList = React.useMemo(() => {
    const valuesList = Object.values(STORE_MANAGER_LIST);

    if (isUniversalSearch) return valuesList;

    return valuesList.filter((store) => store.brand === brand);
  }, [brand]);

  const onClose = () => {
    setDrawer(null);
    if (onStoreSelectorClose) {
      onStoreSelectorClose();
    }
  };

  const displayStoresWithSubHeader = () =>
    BRAND_PROFILES_LIST.map((brandProfile) => (
      <React.Fragment key={brandProfile}>
        <ListSubheader disableSticky>{brandProfile}</ListSubheader>
        {storeList
          .filter((store) => store.brand === brandProfile)
          .map(({ storeId, storeName }) => (
            <ListItem
              className={classes.filterItem}
              key={storeId}
              onClick={() => onUpdateClick([storeId, storeName])}
              data-testid={`store-selection-${storeId}-btn`}
            >
              <ListItemText primary={`${storeId} ${storeName}`} classes={classes.menuText} />
              {storeId === selectedStoreId && <CheckIcon className={classes.checkIcon} />}
            </ListItem>
          ))}
      </React.Fragment>
    ));

  const displayStoresWithoutHeader = () =>
    storeList.map(({ storeId, storeName }) => (
      <ListItem
        className={classes.filterItem}
        key={storeId}
        onClick={() => onUpdateClick([storeId, storeName])}
        data-testid={`store-selection-${storeId}-btn`}
      >
        <ListItemText primary={`${storeId} ${storeName}`} classes={classes.menuText} />
        {storeId === selectedStoreId && <CheckIcon className={classes.checkIcon} />}
      </ListItem>
    ));

  const openStoreDrawer = () => {
    setDrawer({
      content: (
        <FormGroup>
          <List className={classes.storesList} data-testid="store-selection-list">
            {isUniversalSearch ? displayStoresWithSubHeader() : displayStoresWithoutHeader()}
          </List>
        </FormGroup>
      ),
    });
  };

  React.useEffect(() => {
    if (isStoreSelectorOpen) {
      openStoreDrawer();
    } else {
      setDrawer(null);
    }
  }, [isStoreSelectorOpen]);

  return (
    <>
      {!isSilentMode && (
        <Typography
          className={cx(headerMode ? classes.faceWidgetInHeader : classes.faceWidget)}
          variant="body1"
          color="textSecondary"
          onClick={openStoreDrawer}
        >
          {selectedStore.storeName ? selectedStore.storeName : 'Please select a store'}{' '}
          <Arrow className={classes.chevronIconDown} />
        </Typography>
      )}
      <Drawer title="SELECT STORE" open={!!drawer} onClose={onClose}>
        {drawer && drawer.content}
      </Drawer>
    </>
  );
};

StoreSelection.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  setSelectedStore: PropTypes.func,
  selectedStore: PropTypes.objectOf(PropTypes.string),
  brand: PropTypes.string,
  headerMode: PropTypes.bool,
  selectedStoreId: PropTypes.string,
  isSilentMode: PropTypes.bool,
  onStoreSelectorClose: PropTypes.func,
  onSelectStore: PropTypes.func,
};

StoreSelection.defaultProps = {
  brand: '',
  classes: {},
  setSelectedStore: () => {},
  selectedStore: {},
  onStoreSelectorClose: () => {},
  onSelectStore: () => {},
  isSilentMode: false,
};

export default StoreSelection;
